/**
 * Google Display Ad Mockup
 * Simulates how a display ad appears on a webpage
 */

import React from 'react';

interface GoogleDisplayMockupProps {
  children: React.ReactNode;
  adWidth: number;
  adHeight: number;
}

export function GoogleDisplayMockup({ children, adWidth, adHeight }: GoogleDisplayMockupProps) {
  const isLeaderboard = adWidth > adHeight * 3;
  const isSkyscraper = adHeight > adWidth * 2;
  const isRectangle = !isLeaderboard && !isSkyscraper;

  return (
    <div className="bg-[#F8F9FA] rounded-lg overflow-hidden shadow-lg w-full max-w-[700px]">
      {/* Browser chrome */}
      <div className="bg-[#DEE1E6] px-3 py-2 flex items-center gap-2 border-b border-[#C4C7CC]">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#FF5F56]" />
          <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
          <div className="w-3 h-3 rounded-full bg-[#27C93F]" />
        </div>
        <div className="flex-1 mx-4">
          <div className="bg-white rounded px-3 py-1 text-xs text-[#5F6368] flex items-center gap-2">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
            </svg>
            example-website.com
          </div>
        </div>
      </div>

      {/* Webpage content */}
      <div className="p-4">
        {isLeaderboard ? (
          <>
            {/* Leaderboard at top */}
            <div className="mb-4 flex justify-center">
              <div className="relative">
                {children}
                <div className="absolute top-1 right-1 bg-white/90 px-1.5 py-0.5 rounded text-[9px] text-[#5F6368] font-medium">
                  Ad
                </div>
              </div>
            </div>
            {/* Article content below */}
            <div className="space-y-3">
              <div className="h-6 bg-[#202124] rounded w-2/3" />
              <div className="h-3 bg-[#5F6368]/30 rounded w-full" />
              <div className="h-3 bg-[#5F6368]/30 rounded w-full" />
              <div className="h-3 bg-[#5F6368]/30 rounded w-4/5" />
            </div>
          </>
        ) : isSkyscraper ? (
          <div className="flex gap-4">
            {/* Article content */}
            <div className="flex-1 space-y-3">
              <div className="h-6 bg-[#202124] rounded w-3/4" />
              <div className="h-3 bg-[#5F6368]/30 rounded w-full" />
              <div className="h-3 bg-[#5F6368]/30 rounded w-full" />
              <div className="h-3 bg-[#5F6368]/30 rounded w-4/5" />
              <div className="h-32 bg-[#5F6368]/20 rounded" />
              <div className="h-3 bg-[#5F6368]/30 rounded w-full" />
              <div className="h-3 bg-[#5F6368]/30 rounded w-3/4" />
            </div>
            {/* Skyscraper ad on side */}
            <div className="flex-shrink-0 relative">
              {children}
              <div className="absolute top-1 right-1 bg-white/90 px-1.5 py-0.5 rounded text-[9px] text-[#5F6368] font-medium">
                Ad
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Article header */}
            <div className="space-y-3 mb-4">
              <div className="h-6 bg-[#202124] rounded w-3/4" />
              <div className="h-3 bg-[#5F6368]/30 rounded w-full" />
              <div className="h-3 bg-[#5F6368]/30 rounded w-full" />
            </div>
            {/* Rectangle ad inline */}
            <div className="flex justify-center mb-4 relative">
              {children}
              <div className="absolute top-1 right-1 bg-white/90 px-1.5 py-0.5 rounded text-[9px] text-[#5F6368] font-medium">
                Ad
              </div>
            </div>
            {/* More content */}
            <div className="space-y-3">
              <div className="h-3 bg-[#5F6368]/30 rounded w-full" />
              <div className="h-3 bg-[#5F6368]/30 rounded w-4/5" />
              <div className="h-3 bg-[#5F6368]/30 rounded w-full" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
